import React, { useContext, useEffect, useMemo, useState } from "react";
import { arrayHasExactlyOneItem } from "common/utils/arrays";
import { spacing } from "../../stitches.config";
import { HorizontalScrollBar } from "../Common/HorizontalScrollBar/HorizontalScrollBar";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import {
  CancelButton,
  ScopeBarContainer,
  ScopeChip,
} from "./__styles__/SearchScopeBar";
import { SearchContext } from "./SearchContext";
import { ScopeBarItem, SEARCH_SCOPE_BAR_ITEMS } from "./searchScopes";

export const SearchScopeBar = ({
  items = SEARCH_SCOPE_BAR_ITEMS,
}: {
  items?: Array<ScopeBarItem>;
}) => {
  const { setManualScopes } = useContext(SearchContext);

  const hasSingleTopLevel = arrayHasExactlyOneItem(items);

  const [selection, setSelection] = useState<Array<string>>(
    hasSingleTopLevel ? [items[0]!.id] : [],
  );

  const { selectedItems, unselectedChildren } = useMemo(() => {
    const selected: Array<ScopeBarItem> = [];
    let currentLevel: Array<ScopeBarItem> = items;
    for (const id of selection) {
      const found = currentLevel.find(item => item.id === id);
      if (!found) break;
      selected.push(found);
      currentLevel = found.children ?? [];
    }
    return { selectedItems: selected, unselectedChildren: currentLevel };
  }, [items, selection]);

  useEffect(() => {
    const lastItem = selectedItems.at(-1);
    if (!lastItem) return;
    setManualScopes(lastItem.scopes);
  }, [selectedItems, setManualScopes]);

  const selectionKey = selection.join(",");

  return (
    <ScopeBarContainer>
      <HorizontalScrollBar
        scrollKey={selectionKey}
        containerCss={{
          display: "flex",
          alignItems: "center",
          gap: spacing.s,
        }}
        data-testid="search-scope-bar"
      >
        {selection.length === 0 && (
          <ScopeChip selected aria-selected="true" data-testid="scope-chip-all">
            <Body size="default" type="emphasis" color="inherit">
              All
            </Body>
          </ScopeChip>
        )}

        {selectedItems.map((item, index) => {
          const isCancellable = !(hasSingleTopLevel && index === 0);
          return (
            <ScopeChip
              key={item.id}
              selected
              aria-selected="true"
              data-testid={`scope-chip-${item.id}`}
            >
              <Body size="default" type="emphasis" color="inherit">
                {item.label}
              </Body>
              {isCancellable && (
                <CancelButton
                  onPress={() => setSelection(selection.slice(0, index))}
                  aria-label={`Remove ${item.label} filter`}
                >
                  <Icon iconName="x" color="contentSecondary" size={12} />
                </CancelButton>
              )}
            </ScopeChip>
          );
        })}

        {unselectedChildren.map(item => (
          <ScopeChip
            key={item.id}
            role="button"
            onClick={() => setSelection([...selection, item.id])}
            data-testid={`scope-chip-${item.id}`}
          >
            <Body size="default" type="emphasis" color="inherit">
              {item.label}
            </Body>
          </ScopeChip>
        ))}
      </HorizontalScrollBar>
    </ScopeBarContainer>
  );
};
